import React, { useState } from 'react';

interface LoginScreenProps {
  onGuestLogin: () => void;
  onUserLogin: (username: string, password: string) => void;
  isLoading: boolean;
  error: string | null;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onGuestLogin, onUserLogin, isLoading, error }) => {
  const [showUserForm, setShowUserForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUserLogin(username, password);
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-arch-dark p-6">

      {/* Logo */}
      <div className="mb-10 text-center animate-fade-in">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full border-4 border-arch-gold/30 flex items-center justify-center relative">
          <div className="absolute inset-0 border-t-4 border-arch-gold rounded-full animate-spin"></div>
          <div className="w-14 h-14 rounded border-2 border-arch-gold flex items-center justify-center bg-arch-stone">
            <span className="text-arch-gold font-serif font-bold text-3xl">A</span>
          </div>
        </div>
        <h1 className="font-serif text-3xl tracking-widest text-stone-200">
          ARKEO<span className="text-arch-gold">VISION</span>
        </h1>
        <p className="text-stone-500 text-sm mt-2">AI Destekli Arkeoloji Asistanı</p>
      </div>

      {/* Login Options */}
      <div className="w-full max-w-md space-y-4 animate-fade-in">

        {/* Misafir Girişi */}
        <button
          onClick={onGuestLogin}
          disabled={isLoading}
          className="w-full p-6 rounded-xl border border-stone-700 bg-stone-800/50 hover:border-arch-gold/50 hover:bg-stone-800 transition-all group text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-arch-gold/10 border border-arch-gold/30 flex items-center justify-center shrink-0 group-hover:bg-arch-gold/20 transition-colors">
              <svg className="w-6 h-6 text-arch-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-serif text-lg">Misafir Girişi</h3>
              <p className="text-stone-400 text-xs mt-1">
                Müze koleksiyonunu görüntüleyin, VR sahneleri ve 3D modelleri keşfedin
              </p>
            </div>
          </div>
        </button>

        {/* Kullanıcı Girişi */}
        <div className="rounded-xl border border-stone-700 bg-stone-800/50 overflow-hidden transition-all">
          <button
            onClick={() => setShowUserForm(!showUserForm)}
            className="w-full p-6 hover:bg-stone-800 transition-all group text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-arch-gold/10 border border-arch-gold/30 flex items-center justify-center shrink-0 group-hover:bg-arch-gold/20 transition-colors">
                <svg className="w-6 h-6 text-arch-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-serif text-lg">Kullanıcı Girişi</h3>
                <p className="text-stone-400 text-xs mt-1">
                  Tam erişim: kamera tarama, analiz ve tüm özellikler
                </p>
              </div>
              <svg className={`w-5 h-5 text-stone-500 ml-auto transform transition-transform duration-300 ${showUserForm ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {/* Form */}
          <div className={`transition-all duration-500 ease-in-out overflow-hidden ${showUserForm ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <form onSubmit={handleUserSubmit} className="px-6 pb-6 space-y-4 border-t border-stone-700 pt-4">
              <input
                type="text"
                placeholder="Kullanıcı Adı"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-stone-900 border border-stone-600 text-white placeholder-stone-500 focus:border-arch-gold focus:outline-none transition-colors"
                required
              />
              <input
                type="password"
                placeholder="Şifre"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-stone-900 border border-stone-600 text-white placeholder-stone-500 focus:border-arch-gold focus:outline-none transition-colors"
                required
              />
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-arch-gold hover:bg-yellow-600 text-black font-bold rounded-lg transition-all tracking-wider disabled:opacity-50"
              >
                {isLoading ? 'Giriş yapılıyor...' : 'GİRİŞ YAP'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
